import React, { useEffect, useState, useMemo, ReactNode } from 'react';
import { Row, Col, Divider } from 'antd';
import { Form, Input } from 'formik-antd';
import { Formik, FormikProps } from 'formik';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import { map } from 'lodash';

import {
  citiesSelector,
  countriesSelector,
  countryCitiesSelector,
  isCitiesLoadingSelector,
  isCountriesLoadingSelector,
  isCountryCitiesLoadingSelector,
  isStatesLoadingSelector,
  statesSelector,
} from 'redux/selectors/regionSelectors';
import { urlRegex } from 'utils/urlUtils';
import { validateUniqueRecipientEmail } from 'utils/validationUtils';
import Radio from 'components/formik/Radio';
import FormItem from 'components/formik/FormItem';
import Checkbox from 'components/formik/Checkbox';
import FormikEffect from 'components/formik/FormikEffect';
import { SelfEmployedProfileFormValues } from './mapUtils';
import { FullBOUserResponseModel } from 'api/backofficeApiClient';
import { getAllIndustries } from 'redux/reducers/qualificationSlice';
import { userSelector } from 'redux/selectors/authenticationSelectors';
import {
  getAllStates,
  getAllCities,
  getAllCountries,
  getAllCountryCities,
} from 'redux/reducers/regionSlice';
import {
  industriesSelector,
  isIndustriesLoadingSelector,
} from 'redux/selectors/qualificationSelectors';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { countryUSAid } from 'constants/index';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';
import { getInitialCompanyName } from 'utils/companyUtils';

type Props = {
  formRef: any;
  isActive?: boolean;
  formFooter?: ReactNode;
  profileInfo?: FullBOUserResponseModel;
  onSubmit?: (values: SelfEmployedProfileFormValues) => void;
  onChange?: (formik: FormikProps<SelfEmployedProfileFormValues>) => void;
};

const initialValidationSchema = {
  ownerInfo: Yup.object({
    firstName: Yup.string()
      .trim()
      .required('Field is required')
      .max(120, 'First name should be 120 characters maximum'),
    lastName: Yup.string()
      .trim()
      .required('Field is required')
      .max(120, 'Last name should be 120 characters maximum'),
    email: Yup.string()
      .email('Email format is invalid')
      .required('Field is required')
      .max(120, 'Email should be 120 characters maximum'),
  }),
  businessName: Yup.string()
    .trim()
    .required('Field is required')
    .max(250, 'Business name should be 250 characters maximum'),
  businessPhone: Yup.string()
    .trim()
    .required('Field is required')
    .max(120, 'Phone number should be 120 characters maximum'),
  country: Yup.number().required('Field is required'),
  state: Yup.string()
    .trim()
    .max(120, 'State should be 120 characters maximum')
    .when('country', {
      is: (country: number) => checkIsCountriesWithStatesDropdown(country),
      then: Yup.string().required('Field is required'),
    }),
  city: Yup.string()
    .trim()
    .max(120, 'City should be 120 characters maximum')
    .when('country', {
      is: (country: number) => checkIsCountriesWithStatesDropdown(country),
      then: Yup.string().required('Field is required'),
    }),
  internationalStateName: Yup.string()
    .trim()
    .max(120, 'State should be 120 characters maximum')
    .when('country', {
      is: (country: number) => !checkIsCountriesWithStatesDropdown(country),
      then: Yup.string().required('Field is required'),
    }),
  internationalCityName: Yup.string()
    .trim()
    .max(120, 'City should be 120 characters maximum')
    .when('country', {
      is: (country: number) => !checkIsCountriesWithStatesDropdown(country),
      then: Yup.string().required('Field is required'),
    }),
  address: Yup.string()
    .trim()
    .required('Field is required')
    .max(120, `Address should be 120 characters maximum`),
  zip: Yup.string()
    .trim()
    .max(120, 'ZIP code should be 120 characters maximum'),
  industry: Yup.array().of(Yup.string()),
  recipientEmail1: Yup.string()
    .nullable()
    .trim()
    .email('Email format is invalid')
    .max(120, 'Email should be 120 characters maximum')
    .test('duplicate', 'Email must be unique', function (value) {
      const { recipientEmail2, recipientEmail3, ownerInfo } = this.parent;
      const errorMessage = validateUniqueRecipientEmail(
        value,
        [recipientEmail2, recipientEmail3],
        ownerInfo?.email,
      );
      if (errorMessage) {
        return this.createError({ message: errorMessage });
      }
      return true;
    }),
  recipientEmail2: Yup.string()
    .nullable()
    .trim()
    .email('Email format is invalid')
    .max(120, 'Email should be 120 characters maximum')
    .test('duplicate', 'Email must be unique', function (value) {
      const { recipientEmail1, recipientEmail3, ownerInfo } = this.parent;
      const errorMessage = validateUniqueRecipientEmail(
        value,
        [recipientEmail1, recipientEmail3],
        ownerInfo?.email,
      );
      if (errorMessage) {
        return this.createError({ message: errorMessage });
      }
      return true;
    }),
  recipientEmail3: Yup.string()
    .nullable()
    .trim()
    .email('Email format is invalid')
    .max(120, 'Email should be 120 characters maximum')
    .test('duplicate', 'Email must be unique', function (value) {
      const { recipientEmail1, recipientEmail2, ownerInfo } = this.parent;
      const errorMessage = validateUniqueRecipientEmail(
        value,
        [recipientEmail1, recipientEmail2],
        ownerInfo?.email,
      );
      if (errorMessage) {
        return this.createError({ message: errorMessage });
      }
      return true;
    }),
};

const creationValidationSchema = Yup.object({
  ...initialValidationSchema,
  assignmentLocations: Yup.array()
    .of(Yup.number().defined())
    .min(1, 'Field is required'),
  assignment: Yup.boolean(),
  termsAndConditions: Yup.boolean().when('isRegisteredOnMarketPlace', {
    is: true,
    then: Yup.boolean().oneOf(
      [true],
      'Please review and accept the myBasePay Terms and Conditions before proceeding',
    ),
  }),
});

const editValidationSchema = Yup.object({
  ...initialValidationSchema,
  website: Yup.string()
    .matches(urlRegex, 'Website is invalid')
    .max(120, 'Website should be 120 characters maximum'),
});

export default function SelfEmployedProfileForm(props: Props) {
  const {
    formRef,
    profileInfo,
    onSubmit,
    formFooter,
    onChange,
    isActive = false,
  } = props;
  const isEdit = !!profileInfo;
  const dispatch = useAppDispatch();
  const user = useAppSelector(userSelector);
  const countries = useAppSelector(countriesSelector);
  const isCountriesLoading = useAppSelector(isCountriesLoadingSelector);
  const countryCities = useAppSelector(countryCitiesSelector);
  const isCountryCitiesLoading = useAppSelector(isCountryCitiesLoadingSelector);
  const states = useAppSelector(statesSelector);
  const isStatesLoading = useAppSelector(isStatesLoadingSelector);
  const cities = useAppSelector(citiesSelector);
  const isCitiesLoading = useAppSelector(isCitiesLoadingSelector);
  const industries = useAppSelector(industriesSelector);
  const isIndustriesLoading = useAppSelector(isIndustriesLoadingSelector);
  const initialValues = getInitialValues();
  const [selectedCountry, setSelectedCountry] = useState(initialValues.country);
  const [selectedState, setSelectedState] = useState(initialValues.state);

  const filteredStates = useMemo(
    () => states.filter((state) => state.countryId === selectedCountry),
    [states, selectedCountry],
  );

  const statesForAssignmentLocations = useMemo(
    () => states.filter((state) => state.countryId === countryUSAid),
    [states],
  );

  const filteredInternationalCities = useMemo(
    () => countryCities.filter((city) => city.countryId === selectedCountry),
    [selectedCountry, countryCities],
  );

  const filteredCities = useMemo(
    () => cities.filter((city) => city.stateId === selectedState),
    [selectedState, cities],
  );
  useEffect(() => {
    dispatch(getAllStates());
    dispatch(getAllCities());
    dispatch(getAllIndustries());
  }, [dispatch]);

  function getInitialValues(): SelfEmployedProfileFormValues {
    if (!isEdit) {
      return {
        ownerInfo: {
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          email: user?.email || '',
        },
        businessName: getInitialCompanyName(user),
        businessPhone: '',
        country: countryUSAid,
        state: undefined,
        internationalStateName: undefined,
        city: '',
        internationalCityName: undefined,
        address: '',
        zip: '',
        industry: [],
        assignmentLocations: [],
        assignment: true,
        termsAndConditions: false,
        isRegisteredOnMarketPlace: user?.isRegisteredOnMarketPlace,
        recipientEmail1: undefined,
        recipientEmail2: undefined,
        recipientEmail3: undefined,
      };
    }

    const { ownerInfo, businessInfo, website } =
      profileInfo as FullBOUserResponseModel;

    return {
      ownerInfo: {
        firstName: ownerInfo?.firstName || '',
        lastName: ownerInfo?.lastName || '',
        email: ownerInfo?.email || '',
      },
      businessName: businessInfo?.name?.trim() || '',
      businessPhone: businessInfo?.phoneNumber || '',
      country: businessInfo?.location?.countryId || countryUSAid,
      city: businessInfo?.location?.city || '',
      state: businessInfo?.location?.stateId || undefined,
      internationalStateName:
        businessInfo?.location?.internationalStateName || undefined,
      internationalCityName:
        businessInfo?.location?.internationalCityName || undefined,
      address: businessInfo?.location?.address || '',
      zip: businessInfo?.location?.zip || '',
      website: website || '',
      industry: map(
        businessInfo?.industries,
        (industry) => industry.name,
      ) as string[],
      recipientEmail1:
        profileInfo?.activityReportRecipients?.recipientEmail1 || undefined,
      recipientEmail2:
        profileInfo?.activityReportRecipients?.recipientEmail2 || undefined,
      recipientEmail3:
        profileInfo?.activityReportRecipients?.recipientEmail3 || undefined,
    };
  }

  const handleCountryChange = (
    countryId: number,
    formik: FormikProps<SelfEmployedProfileFormValues>,
  ) => {
    setSelectedCountry(countryId);

    formik.setFieldValue('city', '', false);
    formik.setFieldValue('internationalStateName', '', false);
    formik.setFieldValue('internationalCityName', '', false);
    formik.setFieldValue('state', '', false);
    formik.setFieldValue('businessPhone', '', false);
  };

  const handleStateChange = (
    stateId: number,
    formik: FormikProps<SelfEmployedProfileFormValues>,
  ) => {
    setSelectedState(stateId);

    formik.setFieldValue('city', '', false);
    formik.setFieldValue('internationalCityName', '', false);
  };

  useEffect(() => {
    dispatch(getAllCountries());
    dispatch(getAllCountryCities());
    dispatch(getAllStates());
    dispatch(getAllIndustries());
    dispatch(getAllCities());
  }, [dispatch]);

  return (
    <Formik
      enableReinitialize
      innerRef={formRef}
      initialValues={initialValues}
      onSubmit={onSubmit ? onSubmit : () => {}}
      validationSchema={
        isEdit ? editValidationSchema : creationValidationSchema
      }
    >
      {(formik) => (
        <>
          {onChange && (
            <FormikEffect onChange={onChange} disableOnChange={!isActive} />
          )}

          <Form layout="vertical">
            <div className="modal-scroll-wrapper">
              <div className="modal-title">Owner information</div>

              <Row gutter={40}>
                <Col xs={24} md={12}>
                  <Row gutter={20}>
                    <Col xs={24} md={12}>
                      <FormItem name="ownerInfo.firstName" label="First name *">
                        <Input
                          name="ownerInfo.firstName"
                          placeholder="Enter first name"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>

                    <Col xs={24} md={12}>
                      <FormItem name="ownerInfo.lastName" label="Last name *">
                        <Input
                          name="ownerInfo.lastName"
                          placeholder="Enter last name"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>
                  </Row>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem name="ownerInfo.email" label="Email *">
                    <Input
                      name="ownerInfo.email"
                      placeholder="Enter email"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>
              </Row>

              <Divider className="modal-divider after-form" />

              <div className="modal-title">Business information</div>

              <Row gutter={40}>
                <Col xs={24} md={12}>
                  <Row gutter={20}>
                    <Col xs={24} md={12}>
                      <FormItem name="businessName" label="Business name *">
                        <Input
                          name="businessName"
                          placeholder="Enter business name"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>

                    <Col xs={24} md={12}>
                      <FormItem
                        label="Business phone number *"
                        name="businessPhone"
                      >
                        <Input
                          name="businessPhone"
                          placeholder="Enter phone number"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>
                  </Row>
                </Col>

                {isEdit ? (
                  <Col xs={24} md={12}>
                    <Row gutter={20}>
                      <Col xs={24} md={12}>
                        <FormItem name="industry" label="Industry">
                          <FormikSelectWithLoading
                            isLoading={isIndustriesLoading}
                            name="industry"
                            showSearch
                            mode="tags"
                            placeholder="Select industry"
                            optionFilterProp="children"
                          >
                            {industries.map((industry) => (
                              <FormikSelectWithLoading.Option
                                key={`industry-${industry.id}`}
                                value={industry.name || ''}
                              >
                                {industry.name}
                              </FormikSelectWithLoading.Option>
                            ))}
                          </FormikSelectWithLoading>
                        </FormItem>
                      </Col>

                      <Col xs={24} md={12}>
                        <FormItem name="website" label="Website">
                          <Input
                            name="website"
                            placeholder="Enter the website"
                            autoComplete="off"
                          />
                        </FormItem>
                      </Col>
                    </Row>
                  </Col>
                ) : (
                  <Col xs={24} md={12}>
                    <FormItem name="industry" label="Industry">
                      <FormikSelectWithLoading
                        isLoading={isIndustriesLoading}
                        name="industry"
                        showSearch
                        mode="tags"
                        placeholder="Select industry"
                        optionFilterProp="children"
                      >
                        {industries.map((industry) => (
                          <FormikSelectWithLoading.Option
                            key={`industry-${industry.id}`}
                            value={industry.name || ''}
                          >
                            {industry.name}
                          </FormikSelectWithLoading.Option>
                        ))}
                      </FormikSelectWithLoading>
                    </FormItem>
                  </Col>
                )}
              </Row>

              <Row gutter={40}>
                <Col xs={24} md={12}>
                  <Row gutter={20}>
                    <Col xs={24} md={12}>
                      <FormItem name="country" label="Country *">
                        <FormikSelectWithLoading
                          isLoading={isCountriesLoading}
                          name="country"
                          showSearch
                          placeholder="Select head office country"
                          optionFilterProp="children"
                          onChange={(value: number) =>
                            handleCountryChange(value, formik)
                          }
                        >
                          {countries.map((country) => (
                            <FormikSelectWithLoading.Option
                              key={`select-country-${country.id}`}
                              value={country.id || -1}
                            >
                              {country.name}
                            </FormikSelectWithLoading.Option>
                          ))}
                        </FormikSelectWithLoading>
                      </FormItem>
                    </Col>

                    <Col xs={24} md={12}>
                      {checkIsCountriesWithStatesDropdown(
                        formik.values.country,
                      ) ? (
                        <FormItem name="state" label="State *">
                          <FormikSelectWithLoading
                            isLoading={isStatesLoading}
                            name="state"
                            showSearch
                            placeholder="Select head office state"
                            optionFilterProp="children"
                            onChange={(value: number) =>
                              handleStateChange(value, formik)
                            }
                          >
                            {filteredStates.map((state) => (
                              <FormikSelectWithLoading.Option
                                key={`select-state-${state.id}`}
                                value={state.id || -1}
                              >
                                {state.name}
                              </FormikSelectWithLoading.Option>
                            ))}
                          </FormikSelectWithLoading>
                        </FormItem>
                      ) : (
                        <FormItem name="internationalStateName" label="State *">
                          <Input
                            name="internationalStateName"
                            placeholder="Enter head office state"
                            disabled={!formik.values.country}
                            autoComplete="off"
                          />
                        </FormItem>
                      )}
                    </Col>

                    <Col xs={24} md={12}>
                      {checkIsCountriesWithStatesDropdown(
                        formik.values.country,
                      ) ? (
                        <FormItem name="city" label="City *">
                          <FormikAutoCompleteWithLoading
                            isLoading={isCitiesLoading}
                            name="city"
                            filterOption
                            disabled={!formik.values.state}
                            placeholder="Enter head office city"
                            showSearch
                          >
                            {filteredCities.map((city) => (
                              <FormikAutoCompleteWithLoading.Option
                                key={`city-state-${city.id}`}
                                value={city.name || ''}
                              >
                                {city.name}
                              </FormikAutoCompleteWithLoading.Option>
                            ))}
                          </FormikAutoCompleteWithLoading>
                        </FormItem>
                      ) : (
                        <FormItem name="internationalCityName" label="City *">
                          <FormikAutoCompleteWithLoading
                            isLoading={isCountryCitiesLoading}
                            name="internationalCityName"
                            filterOption
                            placeholder="Enter head office city"
                            showSearch
                            disabled={!formik.values.internationalStateName}
                          >
                            {filteredInternationalCities.map((city) => (
                              <FormikAutoCompleteWithLoading.Option
                                key={`company-city-${city.id}`}
                                value={city.name || ''}
                              >
                                {city.name}
                              </FormikAutoCompleteWithLoading.Option>
                            ))}
                          </FormikAutoCompleteWithLoading>
                        </FormItem>
                      )}
                    </Col>
                  </Row>
                </Col>

                <Col xs={24} md={12}>
                  <Row gutter={20}>
                    <Col xs={24} md={12}>
                      <FormItem label="Address *" name="address">
                        <Input
                          name="address"
                          placeholder="Enter address"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>

                    <Col xs={24} md={12}>
                      <FormItem label="ZIP code" name="zip">
                        <Input
                          name="zip"
                          className="full-width"
                          placeholder="Enter ZIP code"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>
                  </Row>
                </Col>
              </Row>

              {!isEdit && (
                <Row gutter={40}>
                  <Col xs={24} md={12}>
                    <FormItem
                      name="assignmentLocations"
                      label="Assignment locations *"
                    >
                      <FormikSelectWithLoading
                        isLoading={isStatesLoading}
                        name="assignmentLocations"
                        showSearch
                        mode="multiple"
                        placeholder="Select states"
                        optionFilterProp="children"
                      >
                        {statesForAssignmentLocations.map((state) => (
                          <FormikSelectWithLoading.Option
                            key={`select-place-${state.id}`}
                            value={state.id || -1}
                          >
                            {state.name}
                          </FormikSelectWithLoading.Option>
                        ))}
                      </FormikSelectWithLoading>
                    </FormItem>
                  </Col>

                  <Col xs={24} md={12}>
                    <span className="hidden-sm-down">
                      <br />
                    </span>

                    <FormItem
                      className="inline-radio"
                      name="assignment"
                      data-id="initialUserAssignmentField"
                      label="Do you have an upcoming assignment?"
                    >
                      <Radio.Group
                        className="radio"
                        name="assignment"
                        options={[
                          { label: 'Yes', value: true },
                          { label: 'No', value: false },
                        ]}
                      />
                    </FormItem>
                  </Col>

                  {formik.values.isRegisteredOnMarketPlace && (
                    <Col xs={24}>
                      <FormItem
                        name="termsAndConditions"
                        className="checkbox-wrapper"
                      >
                        <Checkbox name="termsAndConditions">
                          <span>I understand and agree to the MyBasePay </span>

                          <Link to="/terms-and-conditions" target="_blank">
                            Terms and Conditions.
                          </Link>
                        </Checkbox>
                      </FormItem>
                    </Col>
                  )}
                </Row>
              )}

              <Divider className="modal-divider after-form" />
              <div className="modal-title">Activity Report Recipients</div>
              <Row gutter={40}>
                <Col xs={24} md={12}>
                  <FormItem
                    name="recipientEmail1"
                    label="Activity Report Recipient 1"
                  >
                    <Input
                      name="recipientEmail1"
                      placeholder="Enter email"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    name="recipientEmail2"
                    label="Activity Report Recipient 2"
                  >
                    <Input
                      name="recipientEmail2"
                      placeholder="Enter email"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    name="recipientEmail3"
                    label="Activity Report Recipient 3"
                  >
                    <Input
                      name="recipientEmail3"
                      placeholder="Enter email"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>
              </Row>
            </div>

            {!!formFooter && formFooter}
          </Form>
        </>
      )}
    </Formik>
  );
}
